import { getSoundPack, type SoundPack } from './catalog';
import { SOUND_EFFECTS, defaultSoundMap, type SoundEffectId } from './creatorPacks';
import { soundCharacterProfile, waveformForCharacter } from './soundCharacter';
import type { TableSoundEvent } from '../engine/tables/definition';

export type PinballSound = TableSoundEvent;

type AudioWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
  webkitOfflineAudioContext?: typeof OfflineAudioContext;
};

type OfflineAudioScope = {
  OfflineAudioContext?: typeof OfflineAudioContext;
  webkitOfflineAudioContext?: typeof OfflineAudioContext;
};

interface SoundCue {
  offset: number;
  duration: number;
}

interface RenderedSoundPack {
  buffer: AudioBuffer;
  cues: Partial<Record<SoundEffectId, SoundCue>>;
}

const RENDER_SAMPLE_RATE = 32_000;
const CUE_SLOT_SECONDS = 0.86;
const CUE_DURATION_SECONDS = 0.82;
const CUE_LEAD_SECONDS = 0.025;
const MAX_ACTIVE_VOICES = 24;
const MAX_RENDERED_PACKS = 4;

export interface AudioRenderPlan {
  effects: SoundEffectId[];
  lengthSeconds: number;
}

export interface AudioGraph {
  context: AudioContext;
  master: GainNode;
}

export function buildAudioRenderPlan(effects: readonly SoundEffectId[]): AudioRenderPlan {
  const uniqueEffects = [...new Set(effects)];
  return {
    effects: uniqueEffects,
    lengthSeconds: CUE_LEAD_SECONDS + uniqueEffects.length * CUE_SLOT_SECONDS + 0.05,
  };
}

export function supportsOfflineAudioRendering(scope: OfflineAudioScope): boolean {
  return Boolean(scope.OfflineAudioContext ?? scope.webkitOfflineAudioContext);
}

export function resumeAudioContextSafely(
  context: Pick<AudioContext, 'state' | 'resume'>,
): void {
  if (context.state !== 'suspended') return;
  try {
    void context.resume().catch(() => {
      // Browsers may reject resume() when autoplay policy or a transient audio
      // device/context condition blocks playback. Audio is optional, so keep
      // the game running and let a later user gesture retry naturally.
    });
  } catch {
    // A non-standard implementation can also throw synchronously. Treat that
    // the same as a rejected resume promise: audio stays unavailable for now,
    // but gameplay and rendering remain unaffected.
  }
}

export function createAudioGraphSafely(
  createContext: () => AudioContext,
  muted: boolean,
  desiredVolume: number,
): AudioGraph | null {
  let context: AudioContext | null = null;
  try {
    context = createContext();
    const master = context.createGain();
    const compressor = context.createDynamicsCompressor();
    master.gain.value = muted ? 0 : desiredVolume;
    compressor.threshold.value = -8;
    compressor.knee.value = 10;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.002;
    compressor.release.value = 0.1;
    master.connect(compressor);
    compressor.connect(context.destination);
    return { context, master };
  } catch {
    if (context) {
      try {
        void context.close().catch(() => {
          // Audio initialization already failed. Closing a partially-created
          // context is cleanup only and must not become another startup error.
        });
      } catch {
        // Non-standard implementations can throw from close() as well.
      }
    }
    return null;
  }
}

export function trackAudioSourceWithBudget(
  activeSources: AudioScheduledSourceNode[],
  source: AudioScheduledSourceNode,
  maxVoices = MAX_ACTIVE_VOICES,
): void {
  const budget = Math.max(1, Math.floor(maxVoices));
  while (activeSources.length >= budget) {
    const oldest = activeSources.shift();
    if (!oldest) break;
    try {
      oldest.stop();
    } catch {
      // A source that ended between frames is already harmless.
    }
  }
  activeSources.push(source);
  source.addEventListener('ended', () => {
    const index = activeSources.indexOf(source);
    if (index >= 0) activeSources.splice(index, 1);
  }, { once: true });
}

export class ProceduralPinballAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private renderedPacks = new Map<string, RenderedSoundPack>();
  private renderJobs = new Map<string, Promise<void>>();
  private activeSources: AudioScheduledSourceNode[] = [];
  private muted = false;
  private desiredVolume = 0.34;

  private getContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (this.context?.state === 'closed') {
      this.context = null;
      this.master = null;
      this.noiseBuffer = null;
      this.activeSources.length = 0;
    }
    if (this.context) return this.context;
    const AudioCtor = window.AudioContext ?? (window as AudioWindow).webkitAudioContext;
    if (!AudioCtor) return null;

    const graph = createAudioGraphSafely(
      () => new AudioCtor({ latencyHint: 'interactive' }),
      this.muted,
      this.desiredVolume,
    );
    if (!graph) return null;

    this.context = graph.context;
    this.master = graph.master;
    return graph.context;
  }

  private createNoiseBuffer(context: BaseAudioContext): AudioBuffer {
    const sampleCount = Math.max(1, Math.floor(context.sampleRate));
    const buffer = context.createBuffer(1, sampleCount, context.sampleRate);
    const data = buffer.getChannelData(0);
    let state = 0x9e3779b9;
    for (let index = 0; index < data.length; index += 1) {
      state ^= state << 13;
      state ^= state >>> 17;
      state ^= state << 5;
      data[index] = ((state >>> 0) / 0xffffffff) * 2 - 1;
    }
    return buffer;
  }

  private getLiveNoiseBuffer(context: AudioContext): AudioBuffer {
    if (!this.noiseBuffer) this.noiseBuffer = this.createNoiseBuffer(context);
    return this.noiseBuffer;
  }

  prepare(): void {
    const context = this.getContext();
    if (!context) return;
    this.getLiveNoiseBuffer(context);
  }

  private cachedPack(packId: string): RenderedSoundPack | undefined {
    const rendered = this.renderedPacks.get(packId);
    if (!rendered) return undefined;
    this.renderedPacks.delete(packId);
    this.renderedPacks.set(packId, rendered);
    return rendered;
  }

  private cachedPackHasEffects(packId: string, effects: readonly SoundEffectId[]): boolean {
    const rendered = this.cachedPack(packId);
    return Boolean(rendered && effects.every((effect) => rendered.cues[effect]));
  }

  private rememberPack(packId: string, rendered: RenderedSoundPack): void {
    this.renderedPacks.delete(packId);
    this.renderedPacks.set(packId, rendered);
    while (this.renderedPacks.size > MAX_RENDERED_PACKS) {
      const oldestId = this.renderedPacks.keys().next().value as string | undefined;
      if (!oldestId) break;
      this.renderedPacks.delete(oldestId);
    }
  }

  private trackSource(source: AudioScheduledSourceNode): void {
    trackAudioSourceWithBudget(this.activeSources, source);
  }

  private tone(
    context: BaseAudioContext,
    destination: AudioNode,
    start: number,
    end: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    at: number,
  ): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, start), at);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, end), at + duration);
    gain.gain.setValueAtTime(Math.max(0.0001, volume), at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.02);
    if (context === this.context) this.trackSource(oscillator);
  }

  private noise(
    context: BaseAudioContext,
    destination: AudioNode,
    buffer: AudioBuffer,
    duration: number,
    volume: number,
    highpass: number,
    at: number,
  ): void {
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    filter.type = 'highpass';
    filter.frequency.value = highpass;
    gain.gain.setValueAtTime(Math.max(0.0001, volume), at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    source.start(at, 0.173, Math.min(duration, 0.8));
    if (context === this.context) this.trackSource(source);
  }

  private scheduleEffect(
    context: BaseAudioContext,
    destination: AudioNode,
    noiseBuffer: AudioBuffer,
    effect: SoundEffectId,
    pack: SoundPack,
    offset: number,
  ): void {
    const character = soundCharacterProfile(pack.character);
    const p = pack.pitch * character.pitchScale;
    const bright = pack.brightness * character.brightnessScale;
    const decay = pack.decay * character.decayScale;
    const tone = (
      start: number,
      end: number,
      duration: number,
      volume: number,
      type: OscillatorType,
      delay = 0,
    ) => this.tone(
      context,
      destination,
      start,
      end,
      duration,
      volume * character.toneGain,
      waveformForCharacter(pack.character, type),
      offset + delay,
    );
    const noise = (duration: number, volume: number, highpass = 500, delay = 0) =>
      this.noise(
        context,
        destination,
        noiseBuffer,
        duration,
        volume * character.noiseGain,
        highpass * character.noiseHighpassScale,
        offset + delay,
      );

    switch (effect) {
      case 'coil-clack':
        noise(0.032, 0.12, 900 * bright);
        tone(145 * p, 78 * p, 0.052 * decay, 0.2, 'square');
        break;
      case 'coil-heavy':
        noise(0.045, 0.14, 620 * bright);
        tone(112 * p, 52 * p, 0.075 * decay, 0.22, 'square');
        break;
      case 'escapement-tick':
        noise(0.018, 0.075, 1800 * bright);
        tone(920 * p, 680 * p, 0.026 * decay, 0.11, 'triangle');
        noise(0.016, 0.06, 2100 * bright, 0.042);
        tone(720 * p, 520 * p, 0.032 * decay, 0.09, 'square', 0.042);
        break;
      case 'sonar-return':
        tone(1320 * p, 980 * p, 0.11 * decay, 0.115, 'sine');
        tone(690 * p, 520 * p, 0.18 * decay, 0.065, 'sine', 0.14);
        noise(0.04, 0.026, 2400 * bright, 0.14);
        break;
      case 'telemetry-uplink':
        tone(720 * p, 980 * p, 0.055 * decay, 0.08, 'square');
        tone(940 * p, 1280 * p, 0.065 * decay, 0.072, 'square', 0.075);
        noise(0.024, 0.028, 3200 * bright, 0.145);
        tone(1160 * p, 1840 * p, 0.12 * decay, 0.075, 'triangle', 0.15);
        break;
      case 'reactor-breaker':
        noise(0.026, 0.1, 2600 * bright);
        tone(1380 * p, 360 * p, 0.075 * decay, 0.13, 'sawtooth');
        noise(0.038, 0.07, 1500 * bright, 0.065);
        tone(520 * p, 118 * p, 0.16 * decay, 0.16, 'square', 0.07);
        break;
      case 'spectral-capture':
        noise(0.22, 0.036, 2100 * bright);
        tone(260 * p, 760 * p, 0.28 * decay, 0.085, 'sine');
        tone(640 * p, 230 * p, 0.26 * decay, 0.065, 'triangle', 0.12);
        noise(0.08, 0.024, 3400 * bright, 0.18);
        break;
      case 'dragon-breath':
        tone(180 * p, 72 * p, 0.24 * decay, 0.13, 'sawtooth');
        tone(390 * p, 145 * p, 0.16 * decay, 0.07, 'square', 0.035);
        noise(0.18, 0.08, 980 * bright, 0.045);
        noise(0.09, 0.035, 2800 * bright, 0.13);
        break;
      case 'temple-heart':
        noise(0.035, 0.08, 1200 * bright);
        tone(220 * p, 160 * p, 0.11 * decay, 0.13, 'triangle');
        tone(660 * p, 990 * p, 0.24 * decay, 0.09, 'sine', 0.035);
        tone(990 * p, 740 * p, 0.22 * decay, 0.06, 'sine', 0.11);
        break;
      case 'pit-service':
        noise(0.022, 0.095, 1900 * bright);
        tone(240 * p, 155 * p, 0.055 * decay, 0.1, 'square');
        noise(0.018, 0.075, 2500 * bright, 0.055);
        tone(205 * p, 135 * p, 0.05 * decay, 0.085, 'square', 0.055);
        noise(0.12, 0.045, 1100 * bright, 0.105);
        tone(118 * p, 86 * p, 0.13 * decay, 0.07, 'sawtooth', 0.1);
        break;
      case 'steam-brake':
        noise(0.032, 0.11, 720 * bright);
        tone(190 * p, 82 * p, 0.12 * decay, 0.15, 'square');
        tone(540 * p, 260 * p, 0.075 * decay, 0.07, 'triangle', 0.035);
        noise(0.26, 0.055, 1700 * bright, 0.08);
        tone(104 * p, 72 * p, 0.2 * decay, 0.055, 'sawtooth', 0.09);
        break;
      case 'pop-chime':
        tone(680 * p, 260 * p, 0.14 * decay, 0.23, 'sine');
        tone(1320 * p, 620 * p, 0.08 * decay, 0.09, 'triangle');
        break;
      case 'pop-deep':
        tone(430 * p, 150 * p, 0.15 * decay, 0.25, 'sine');
        noise(0.03, 0.08, 1100 * bright);
        break;
      case 'rubber-snap':
        noise(0.05, 0.13, 1500 * bright);
        tone(390 * p, 165 * p, 0.075 * decay, 0.15, 'square');
        break;
      case 'target-knock':
        noise(0.035, 0.12, 1900 * bright);
        tone(760 * p, 430 * p, 0.05 * decay, 0.1, 'triangle');
        break;
      case 'target-zap':
        tone(1120 * p, 390 * p, 0.07 * decay, 0.12, 'sawtooth');
        tone(620 * p, 980 * p, 0.09 * decay, 0.07, 'sine');
        break;
      case 'lane-ping':
        tone(1020 * p, 1380 * p, 0.085 * decay, 0.11, 'sine');
        break;
      case 'lane-bleep':
        tone(820 * p, 1180 * p, 0.07 * decay, 0.1, 'square');
        break;
      case 'ramp-rise':
        tone(360 * p, 1120 * p, 0.22 * decay, 0.1, 'sine');
        tone(720 * p, 1480 * p, 0.16 * decay, 0.045, 'triangle', 0.055);
        break;
      case 'ramp-bell':
        tone(620 * p, 920 * p, 0.16 * decay, 0.11, 'triangle');
        tone(930 * p, 1395 * p, 0.2 * decay, 0.07, 'sine', 0.08);
        break;
      case 'plunger-spring':
        noise(0.065, 0.09, 420);
        tone(105 * p, 48 * p, 0.13 * decay, 0.2, 'triangle');
        break;
      case 'nudge-thump':
        tone(78 * p, 42 * p, 0.085, 0.2, 'sine');
        noise(0.028, 0.05, 240);
        break;
      case 'drain-drop':
        tone(230 * p, 52 * p, 0.5 * decay, 0.19, 'sine');
        break;
      case 'save-rise':
        tone(540 * p, 880 * p, 0.14 * decay, 0.12, 'triangle');
        tone(760 * p, 1240 * p, 0.18 * decay, 0.08, 'sine', 0.07);
        break;
      case 'tilt-buzz':
        tone(92, 72, 0.55, 0.22, 'sawtooth');
        noise(0.35, 0.045, 1000);
        break;
      case 'start-fanfare':
        tone(330 * p, 440 * p, 0.12, 0.09, 'triangle');
        tone(440 * p, 660 * p, 0.13, 0.1, 'triangle', 0.09);
        tone(660 * p, 990 * p, 0.18, 0.11, 'triangle', 0.18);
        break;
      case 'gameover-drop':
        tone(440 * p, 220 * p, 0.22, 0.11, 'triangle');
        tone(330 * p, 110 * p, 0.34, 0.1, 'sine', 0.16);
        break;
      case 'jackpot-fanfare':
        tone(440 * p, 880 * p, 0.16 * decay, 0.12, 'triangle');
        tone(660 * p, 1320 * p, 0.18 * decay, 0.11, 'triangle', 0.09);
        tone(880 * p, 1760 * p, 0.22 * decay, 0.1, 'triangle', 0.18);
        break;
      case 'boing':
        tone(190, 68, 0.18, 0.24, 'sine');
        tone(420, 135, 0.14, 0.1, 'triangle', 0.025);
        break;
      case 'fart-pop':
        tone(118, 42, 0.24, 0.25, 'sawtooth');
        tone(78, 34, 0.3, 0.15, 'square', 0.055);
        noise(0.18, 0.075, 65, 0.02);
        break;
      case 'dog-bark':
        noise(0.075, 0.11, 380);
        tone(245, 115, 0.085, 0.18, 'square');
        noise(0.065, 0.09, 430, 0.115);
        tone(210, 98, 0.075, 0.15, 'square', 0.11);
        break;
      case 'slide-whistle':
        tone(310, 1550, 0.32, 0.13, 'sine');
        tone(1550, 420, 0.38, 0.13, 'sine', 0.32);
        break;
      case 'chicken-squawk':
        tone(980, 360, 0.12, 0.16, 'sawtooth');
        tone(1320, 510, 0.14, 0.12, 'square', 0.08);
        tone(1080, 300, 0.15, 0.12, 'sawtooth', 0.17);
        break;
      case 'cat-meow':
        tone(420, 690, 0.12, 0.13, 'sine');
        tone(690, 470, 0.2, 0.15, 'sine', 0.1);
        tone(860, 520, 0.17, 0.055, 'triangle', 0.12);
        break;
      case 'cat-hiss':
        noise(0.34, 0.13, 2600);
        tone(180, 120, 0.26, 0.035, 'sawtooth');
        break;
      case 'cat-purr':
        tone(62, 55, 0.16, 0.17, 'sine');
        tone(68, 58, 0.16, 0.17, 'sine', 0.16);
        tone(64, 54, 0.16, 0.17, 'sine', 0.32);
        break;
    }
  }

  private async renderPack(effects: readonly SoundEffectId[], packId?: string): Promise<void> {
    if (typeof window === 'undefined') return;
    const pack = getSoundPack(packId);
    const existing = this.cachedPack(pack.id);
    const existingEffects = existing
      ? Object.keys(existing.cues) as SoundEffectId[]
      : [];
    const plan = buildAudioRenderPlan([...existingEffects, ...effects]);
    if (existing && plan.effects.length === existingEffects.length) return;

    const OfflineCtor = window.OfflineAudioContext ?? (window as AudioWindow).webkitOfflineAudioContext;
    if (!OfflineCtor) return;
    const offline = new OfflineCtor(1, Math.ceil(plan.lengthSeconds * RENDER_SAMPLE_RATE), RENDER_SAMPLE_RATE);
    const noiseBuffer = this.createNoiseBuffer(offline);
    const cues: Partial<Record<SoundEffectId, SoundCue>> = {};

    plan.effects.forEach((effect, index) => {
      const offset = CUE_LEAD_SECONDS + index * CUE_SLOT_SECONDS;
      cues[effect] = { offset, duration: CUE_DURATION_SECONDS };
      this.scheduleEffect(offline, offline.destination, noiseBuffer, effect, pack, offset);
    });

    const buffer = await offline.startRendering();
    this.rememberPack(pack.id, { buffer, cues });
  }

  async prepareEffects(
    effects: readonly SoundEffectId[] = SOUND_EFFECTS.map((effect) => effect.id),
    packId?: string,
  ): Promise<void> {
    if (
      effects.length === 0
      || typeof window === 'undefined'
      || !supportsOfflineAudioRendering(window as AudioWindow)
    ) return;
    const pack = getSoundPack(packId);
    const requested = buildAudioRenderPlan(effects).effects;

    while (!this.cachedPackHasEffects(pack.id, requested)) {
      const existingJob = this.renderJobs.get(pack.id);
      if (existingJob) {
        await existingJob;
        continue;
      }

      const pending = this.renderPack(requested, pack.id)
        .catch(() => {
          // The atlas is only an optimization. Unsupported or transient offline
          // rendering failures must leave the live-synthesis fallback usable
          // without surfacing an unhandled rejection into gameplay.
        })
        .finally(() => {
          if (this.renderJobs.get(pack.id) === pending) this.renderJobs.delete(pack.id);
        });
      this.renderJobs.set(pack.id, pending);
      await pending;

      if (!this.cachedPackHasEffects(pack.id, requested)) return;
    }
  }

  unlock(): void {
    const context = this.getContext();
    if (!context) return;
    resumeAudioContextSafely(context);
  }

  private ensureRunning(context: AudioContext): void {
    resumeAudioContextSafely(context);
  }

  private playRendered(context: AudioContext, rendered: RenderedSoundPack, effect: SoundEffectId): boolean {
    if (!this.master) return false;
    const cue = rendered.cues[effect];
    if (!cue) return false;
    const source = context.createBufferSource();
    source.buffer = rendered.buffer;
    source.playbackRate.value = 0.992 + Math.random() * 0.016;
    source.connect(this.master);
    this.trackSource(source);
    source.start(context.currentTime, cue.offset, cue.duration);
    return true;
  }

  private playLiveFallback(context: AudioContext, effect: SoundEffectId, packId?: string): void {
    if (!this.master) return;
    this.scheduleEffect(
      context,
      this.master,
      this.getLiveNoiseBuffer(context),
      effect,
      getSoundPack(packId),
      context.currentTime,
    );
  }

  playEffect(effect: SoundEffectId, packId?: string): void {
    if (this.muted || this.desiredVolume <= 0) return;
    const context = this.getContext();
    if (!context) return;
    this.ensureRunning(context);
    const pack = getSoundPack(packId);
    const rendered = this.cachedPack(pack.id);
    if (rendered && this.playRendered(context, rendered, effect)) return;

    this.playLiveFallback(context, effect, pack.id);
    void this.prepareEffects([effect], pack.id);
  }

  play(sound: PinballSound, packId?: string): void {
    this.playEffect(defaultSoundMap(packId)[sound], packId);
  }

  setVolume(value: number): void {
    this.desiredVolume = Math.max(0, Math.min(1, value));
    if (this.master) this.master.gain.value = this.muted ? 0 : this.desiredVolume;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : this.desiredVolume;
  }
}

export const pinballAudio = new ProceduralPinballAudio();